// Раздел «Суточные расходы» (адрес `/money`, до 01.10.2026 назывался «Деньги»). Пять
// представлений: пульт подотчёта, очередь приёмки заявок, «Кому выдано» (реестр выдач за месяц),
// «На что потрачено» (расход по категориям со сверкой месяца) и архив переписки — и каждое
// показывается только тому, у кого есть на него право (см. VIEW_PERMISSION).
//
// Пульт — стартовый экран финансового директора: вход открывает его, и без единого клика
// видно, у кого сколько висит. Правая колонка без выбранной строки показывает не заглушку
// «выберите человека», а настоящую сводку за период: это и есть «утро, ноль кликов».
//
// Полевой руководитель заходит сюда же и по тем же адресам. Третьего экрана для него нет:
// сервер отдаёт ему выборку из одной строки — его самого (правило 12, изоляция делается
// выборкой, а не скрытием кнопок).

import { RiCompass3Line } from '@remixicon/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import type { SearchState } from '@/shared/routing/search';
import { parseSearch } from '@/shared/routing/search';
import * as LinkButton from '@/shared/ui/link-button';
import { CardTabsList } from '@/shared/ui/card-tabs';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';

// Побочный эффект: раздел объявляет каркасу свои панели. Импорт обязан быть здесь —
// иначе стопка панелей покажет честную заглушку вместо мастера.
import './panels/register';

import { PersonCard } from './card/PersonCard';
import { usePerson } from './queries';
import { СводкаПодотчёта } from './components/СводкаПодотчёта';
import {
  FILTER_ENTITY,
  MONTHLY_VIEWS,
  PANEL_PERSON_SETTINGS,
  VIEW_ARCHIVE,
  VIEW_CLAIMS,
  VIEW_DESK,
  VIEW_FLOW,
  VIEW_ISSUES,
  carryFilters,
  parseTab,
  parseView,
  personPath,
  type MoneyView,
  viewPath,
} from './route';
import { AccountableDesk } from './views/AccountableDesk';
import { CashFlow } from './views/CashFlow';
import { CashIssues } from './views/CashIssues';
import { ChatArchive } from './views/ChatArchive';
import { ClaimsQueue } from './views/ClaimsQueue';

/**
 * Право, по которому представление вообще показывается в полосе вкладок.
 *
 * Заведено 15.09.2026. До этого дня три вкладки рисовались безусловно, а право проверялось
 * только на кнопке внутри — и охранник с полевым руководителем видели «Очередь приёмки»:
 * чужую работу, где им нечего решать. По их правам сервер отдаёт туда выборку из их
 * собственных заявок, то есть экран отвечал им либо пустотой, либо пересказом того, что они
 * и так отправили с телефона. Вкладка, за которой ждёт чужая работа, — это тот же отказ,
 * только на два нажатия позже. Так же собираются вкладки в «Расчёте» и в «Найме».
 *
 * Раздача взята с сервера (`server/app/api/v1/accountable.py`, `server/app/api/v1/reports.py`),
 * а не выдумана здесь:
 *
 *   * пульт подотчёта — `core.accountable.view`, оно же право на весь раздел;
 *   * очередь приёмки — право РЕШАТЬ по заявке. Смотреть заявки может любой подотчётный, но
 *     очередь существует ради решения: без него это список чужих чеков;
 *   * расход по категориям — отчёт, и сервер требует к нему право на отчёты сверх подотчёта.
 */
const VIEW_PERMISSION: Readonly<Record<MoneyView, string>> = {
  [VIEW_DESK]: 'core.accountable.view',
  [VIEW_CLAIMS]: 'core.accountable.claim_accept',
  // «Кому выдано» — реестр выдач, то есть тоже отчёт: право то же, что у «На что потрачено».
  // Маршрут сервера при этом открыт по праву на подотчёт: у операционного руководства право на
  // отчёты есть, а подотчёта нет вовсе (он скрыт целиком), и прямая ссылка ему ответит отказом
  // сервера. Лишнего вкладка не раскрывает: данные приходят только с сервера.
  [VIEW_ISSUES]: 'core.report.view',
  [VIEW_FLOW]: 'core.report.view',
  // Архив — уровень денег, а не переписки: там зарплаты, авансы и номера карт всей компании
  // без разреза по объектам. Право есть у администратора, финотдела и руководства; полевой
  // руководитель и охранник вкладки не увидят, а дверь ответит им отказом.
  [VIEW_ARCHIVE]: 'core.chat_archive.view',
};

/** Названия в полосе вкладок. */
const VIEW_TITLES: Readonly<Record<MoneyView, string>> = {
  [VIEW_DESK]: 'Подотчёт',
  [VIEW_CLAIMS]: 'Очередь приёмки',
  // Слова самого заказчика: «что-то ему дали и куда он их потратил». Первая половина вопроса.
  [VIEW_ISSUES]: 'Кому выдано',
  // Раньше «Расход». Заказчик спрашивает «куда он их потратил», и вкладка отвечает ровно на
  // это, словами его вопроса. Адрес `/money/flow` остался: ссылки на него уже разосланы.
  [VIEW_FLOW]: 'На что потрачено',
  [VIEW_ARCHIVE]: 'Архив переписки',
};

/** Порядок вкладок — порядок объявления здесь. */
const VIEWS: readonly MoneyView[] = [VIEW_DESK, VIEW_CLAIMS, VIEW_ISSUES, VIEW_FLOW, VIEW_ARCHIVE];

const ISSUE_PERMISSION = 'core.accountable.issue';
const REVERSE_PERMISSION = 'core.accountable.reverse';
const RETURN_PERMISSION = 'core.accountable.return';
const TRANSFER_PERMISSION = 'core.accountable.transfer';
const CLAIM_PERMISSION = 'core.accountable.claim_create';

export function MoneySection({ view: viewFromShell }: { view?: string }) {
  const params = useParams<{ view?: string; rowId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  // Берём готовое поле хранилища, а НЕ `state.user?.permissions ?? []`. Разница не
  // стилистическая: `?? []` создаёт новый пустой массив на каждый вызов селектора, а zustand 5
  // стоит на useSyncExternalStore — React сравнивает снимки по ссылке, видит новую и рисует
  // снова, и так без конца. Пока человек вошёл, ветка `??` не срабатывает и всё выглядит
  // исправным; ломается ровно в момент, когда `user` становится пустым — истёк токен или
  // человек вышел с открытым разделом «Суточные расходы». Вкладка вместо перехода на вход упирается в
  // «Maximum update depth exceeded» и съедает процессор.
  const права = useAuthStore((state) => state.permissions);

  const rawView = params.view ?? viewFromShell;
  const search = parseSearch(location.search);
  const разобранный = parseView(rawView);
  const rowId = params.rowId ?? null;

  if (разобранный === null) {
    return (
      <main className='flex h-full flex-1 items-center justify-center bg-bg-weak-50'>
        <EmptyState
          icon={RiCompass3Line}
          title={`Представления «${rawView ?? ''}» в разделе «Суточные расходы» нет`}
          description='В разделе пульт подотчёта, очередь приёмки заявок, «Кому выдано», «На что потрачено» и архив переписки. Похоже, ссылка устарела или в ней опечатка.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to={viewPath(VIEW_DESK)}>К пульту подотчёта</Link>
            </LinkButton.Root>
          }
        />
      </main>
    );
  }

  const view: MoneyView = разобранный;

  function поставитьОтбор(key: string, value: string | null) {
    const фильтры = { ...search.filters };
    if (value === null) {
      delete фильтры[key];
    } else {
      фильтры[key] = value;
    }
    void navigate(viewPath(view, { ...search, filters: фильтры, panels: [] }));
  }

  function снятьОтборы(keys: readonly string[]) {
    // Несколько отборов снимаются ОДНИМ переходом: по одному они затирали бы друг друга, потому
    // что каждый считает адрес от текущего, а не от уже изменённого.
    const фильтры = { ...search.filters };
    for (const key of keys) {
      delete фильтры[key];
    }
    void navigate(viewPath(view, { ...search, filters: фильтры, panels: [] }));
  }

  function поставитьПоиск(value: string) {
    void navigate(viewPath(view, { ...search, q: value === '' ? undefined : value }));
  }

  // Полоса вкладок, а не сегментированный переключатель. Сегментированный делит доступную
  // ширину РАВНЫМИ долями и запрещает перенос: в колонке списка «Очередь приёмки» требовала
  // 115 точек при доле в 106 и лезла на соседа (замер 03.09.2026). У полосы вкладок ширина по
  // содержимому, а обёртка прокручивается — наложение невозможно по устройству, а не по
  // расчёту, что «названия короткие и поместятся». Держит это `e2e/переключатель-читается`.
  //
  // Состав вкладок — по правам. Открытое по прямой ссылке представление, до чьего права
  // человек не дотянулся, из полосы НЕ выкидывается: иначе он видит экран, а активной вкладки
  // нет, и полоса читается как сломанная. Тот же приём, что в переключателе «Найма».
  const доступные = VIEWS.filter((пункт) => can(права, VIEW_PERMISSION[пункт]));
  const вкладки = доступные.includes(view) ? доступные : [...доступные, view];
  const переключатель = (
    <TabMenu.Root
      value={view}
      onValueChange={(next) =>
        void navigate(
          viewPath(
            next as MoneyView,
            // Месяц переходит только между «Кому выдано» и «На что потрачено»: это две половины
            // одного вопроса, и май на одной вкладке обязан остаться маем на другой.
            carryFilters(search, {
              месяц: MONTHLY_VIEWS.includes(view) && MONTHLY_VIEWS.includes(next as MoneyView),
            }),
          ),
        )
      }
    >
      {/* Полоса стоит ОДНА над обеими колонками, на всю ширину раздела, как в «Расчёте». До
          01.10.2026 на пульте и в очереди она жила в колонке списка (352 точки при потребных 570):
          видны были «Подотчёт», «Очередь приёмки» и полвкладки «Кому выдано», а «На что
          потрачено», то есть половина того, о чём просил заказчик, пряталась за стрелкой и
          требовала двух нажатий (замер независимой проверки). На всю ширину влезают все пять.
          Стрелки прокрутки у `CardTabsList` остаются на случай совсем узкого окна: они
          появляются ТОЛЬКО когда есть куда крутить. */}
      <CardTabsList className='h-10 gap-5 border-t-0'>
        {вкладки.map((пункт) => (
          <TabMenu.Trigger key={пункт} value={пункт}>
            {VIEW_TITLES[пункт]}
          </TabMenu.Trigger>
        ))}
      </CardTabsList>
    </TabMenu.Root>
  );

  return (
    <div className='flex h-full min-h-0 w-full flex-col'>
      {/* Отступы те же, что у полосы «Расчёта»: поле 16 в узком окне, а не дрожание текста при
          переходе из раздела в раздел. */}
      <div className='flex shrink-0 items-center border-b border-stroke-soft-200 bg-bg-white-0 px-5 py-2 узко:px-4'>
        {переключатель}
      </div>
      <div className='flex min-h-0 w-full min-w-0 flex-1'>
        {view === VIEW_DESK ? (
          <AccountableDesk
            search={search}
            selectedId={rowId}
            onFilter={поставитьОтбор}
            onQuery={поставитьПоиск}
          />
        ) : view === VIEW_ISSUES ? (
          <CashIssues search={search} onFilter={поставитьОтбор} onClearFilters={снятьОтборы} />
        ) : view === VIEW_FLOW ? (
          <CashFlow search={search} onFilter={поставитьОтбор} onClearFilters={снятьОтборы} />
        ) : view === VIEW_ARCHIVE ? (
          <ChatArchive search={search} onFilter={поставитьОтбор} onQuery={поставитьПоиск} />
        ) : (
          <ClaimsQueue
            search={search}
            onFilter={поставитьОтбор}
            onOpenClaim={(claimId) =>
              void navigate(
                viewPath(VIEW_CLAIMS, {
                  ...search,
                  panels: [...search.panels, { view: 'claim', id: claimId }],
                }),
              )
            }
          />
        )}

        {view === VIEW_DESK && rowId !== null ? (
          <Карточка
            personId={rowId}
            search={search}
            canIssue={can(права, ISSUE_PERMISSION)}
            canReverse={can(права, REVERSE_PERMISSION)}
            canReturn={can(права, RETURN_PERMISSION)}
            canTransfer={can(права, TRANSFER_PERMISSION)}
            canClaim={can(права, CLAIM_PERMISSION)}
            onTab={(tab) => void navigate(personPath(rowId, { ...search, tab }))}
            onPanel={(вид) =>
              void navigate(
                personPath(rowId, {
                  ...search,
                  panels: [...search.panels, { view: вид, id: rowId }],
                }),
              )
            }
          />
        ) : view === VIEW_FLOW || view === VIEW_ARCHIVE || view === VIEW_ISSUES ? null : (
          // Сводка подотчёта стоит рядом с пультом и очередью — там она отвечает на вопрос
          // «а сколько всего». На «Расходе» она была бы вторым ответом про те же деньги рядом
          // с первым, причём разложенным иначе: два разреза одной суммы на одном экране
          // человек читает как расхождение. То же и у «Кому выдано»: там свой итог за месяц, и
          // вторая сумма рядом стояла бы чужой. В архиве переписки денег нет вовсе: там ищут слова,
          // и сумма подотчёта сбоку отвечала бы на незаданный вопрос.
          <Сводка legalEntityId={search.filters[FILTER_ENTITY]} />
        )}
      </div>
    </div>
  );
}

function Сводка({ legalEntityId }: { legalEntityId: string | undefined }) {
  // Обёртка в колонку детали — единственное, что отличает этот случай от панели на планшете.
  return (
    <DetailPane>
      <СводкаПодотчёта legalEntityId={legalEntityId} />
    </DetailPane>
  );
}

function Карточка({
  personId,
  search,
  canIssue,
  canReverse,
  canReturn,
  canTransfer,
  canClaim,
  onTab,
  onPanel,
}: {
  personId: string;
  search: SearchState;
  canIssue: boolean;
  canReverse: boolean;
  canReturn: boolean;
  canTransfer: boolean;
  canClaim: boolean;
  onTab: (tab: string) => void;
  onPanel: (вид: string) => void;
}) {
  const запрос = usePerson(personId);
  return (
    <ScreenState
      data={запрос.data}
      error={запрос.error as never}
      subject='карточку подотчётного лица'
      isEmpty={() => false}
      loading={
        <DetailPane>
          <div className='flex flex-col gap-3 p-5'>
            <Skeleton className='h-10 w-64' />
            <Skeleton className='h-24 w-full' />
          </div>
        </DetailPane>
      }
      empty={null}
      onRetry={() => void запрос.refetch()}
    >
      {(человек) => (
        <PersonCard
          person={человек}
          tab={parseTab(search.tab)}
          onTab={onTab}
          canIssue={canIssue}
          canReverse={canReverse}
          canReturn={canReturn}
          canTransfer={canTransfer}
          canClaim={canClaim}
          onIssue={() => onPanel('issue')}
          onReturn={() => onPanel('return')}
          onTransfer={() => onPanel('transfer')}
          onNewClaim={() => onPanel('new-claim')}
          onSettings={() => onPanel(PANEL_PERSON_SETTINGS)}
        />
      )}
    </ScreenState>
  );
}
